import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { DomainCardButton } from '../../domain-cards/ui';

/**
 * DowntimeMovePattern - Player downtime action that rolls a die to replace another roll.
 *
 * Use cases:
 * - Age of Umbra: Keep Watch (roll Hope Die to replace GM's Lurking Darkness d12)
 *
 * Config properties:
 * - displayName: UI label
 * - description: explanatory text
 * - icon: emoji icon
 * - rollType: 'hope-die' (resolves from character.hopeDie) or 'd6'/'d8' etc.
 * - replacesRoll: mechanic ID this can replace (e.g., 'lurking-darkness')
 * - replacesRollName: display name of the replaced roll
 * - multiplePlayersRule: 'take-highest' | 'take-lowest' | 'first-only'
 * - colorClass: Tailwind classes
 */
const DowntimeMovePattern = ({
  frameId,
  mechanicId,
  config,
  character,
  updateCharacter,
  onClose,
}) => {
  const [rollResult, setRollResult] = useState(null);
  const [isRolling, setIsRolling] = useState(false);
  const [hasCommitted, setHasCommitted] = useState(false);

  const resolveDieType = useCallback(() => {
    if (config.rollType === 'hope-die') return character?.hopeDie || 'd6';
    return config.rollType || 'd6';
  }, [config.rollType, character]);

  const rollDie = useCallback((dieStr) => {
    const sides = parseInt(dieStr.replace('d', ''), 10) || 6;
    return Math.ceil(Math.random() * sides);
  }, []);

  const handleRoll = useCallback(() => {
    setIsRolling(true);
    setHasCommitted(false);

    setTimeout(() => {
      const dieType = resolveDieType();
      const roll = rollDie(dieType);
      setRollResult({ dieType, roll });
      setIsRolling(false);
      toast.success(`Rolled ${roll} on ${dieType.toUpperCase()}`, { icon: config.icon || '👁️' });
    }, 800);
  }, [resolveDieType, rollDie, config.icon]);

  const handleUseRoll = useCallback(() => {
    setHasCommitted(true);
    toast.success(
      `Using ${rollResult.roll} to replace the ${config.replacesRollName || 'GM roll'}`,
      { icon: '🛡️', duration: 4000 }
    );
  }, [rollResult, config.replacesRollName]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-xl font-bold text-purple-400 mb-1">
          {config.icon || '👁️'} {config.displayName || 'Downtime Move'}
        </h3>
        {config.description && (
          <p className="text-sm text-gray-400">{config.description}</p>
        )}
      </div>

      {/* Die Display */}
      <div className={`relative p-6 rounded-lg border-2 text-center ${config.colorClass || 'bg-gray-900/20 border-gray-600'}`}>
        {rollResult ? (
          <>
            <div className="text-5xl font-bold text-purple-400 mb-2">
              {rollResult.roll}
            </div>
            <p className="text-sm text-purple-300">
              Rolled {rollResult.dieType.toUpperCase()}
            </p>
          </>
        ) : (
          <p className="text-sm text-gray-400">
            Roll your {resolveDieType().toUpperCase()} to keep watch
          </p>
        )}

        {isRolling && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
            <div className="text-center">
              <div className="text-4xl animate-bounce mb-2">🎲</div>
              <p className="text-sm text-purple-400 font-medium">Rolling...</p>
            </div>
          </div>
        )}
      </div>

      {/* Multiple players note */}
      {config.multiplePlayersRule === 'take-highest' && (
        <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-600">
          <p className="text-xs text-gray-400 italic">
            If multiple players keep watch, take the highest roll.
          </p>
        </div>
      )}

      {/* Committed confirmation */}
      {hasCommitted && (
        <div className="bg-green-900/20 p-4 rounded-lg border border-green-600/50">
          <p className="text-sm font-bold text-green-300">
            🛡️ Roll of {rollResult.roll} committed — replaces the {config.replacesRollName || 'GM roll'}
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2">
        <DomainCardButton
          onClick={handleRoll}
          disabled={isRolling || hasCommitted}
          variant="primary"
          className="flex-1"
        >
          {rollResult && !hasCommitted ? 'Reroll' : `Roll ${resolveDieType().toUpperCase()}`}
        </DomainCardButton>

        {rollResult && !hasCommitted && (
          <DomainCardButton
            onClick={handleUseRoll}
            variant="activeSuccess"
            className="flex-1"
          >
            Use This Roll
          </DomainCardButton>
        )}
      </div>

      {/* Close */}
      <DomainCardButton onClick={onClose} variant="secondary" className="w-full">
        Close
      </DomainCardButton>
    </div>
  );
};

export default DowntimeMovePattern;
